import React from "react";
import Button from "@mui/material/Button";

/**
 * StyledSubmitButton Props
 * ------------------------
 * onClick       - Callback for click events
 * children      - Button text or nested content
 * themeMode     - Light or dark theme, used to style background colors
 * isMobile      - Adjusts layout for mobile responsiveness
 * minWidthAuto  - If true, disables minWidth constraints
 * disabled      - Disables the button when true
 */
type Props = {
    onClick: (e: any) => void;
    children: React.ReactNode;
    themeMode?: "light" | "dark";
    isMobile?: boolean;
    minWidthAuto?: boolean;
    disabled?: boolean;
};

/**
 * StyledSubmitButton Component
 * ---------------------------
 * A themed, responsive submit button built using Material UI's Button.
 * - Supports light/dark mode color variations
 * - Fully responsive (mobile vs desktop)
 * - Allows flexible width and minWidth behavior
 * - Styles overridden strongly using "!important" for guaranteed consistency
 */
const StyledSubmitButton: React.FC<Props> = ({ onClick, children, themeMode, isMobile, minWidthAuto, disabled }) => (
    <Button
        variant="contained"
        sx={{
            marginTop: 12,

            /* Full width on mobile, fixed width on desktop */
            width: isMobile ? "100% !important" : "10% !important",

            /* Optional override for minWidth behavior */
            minWidth: minWidthAuto
                ? "none !important"
                : isMobile
                ? "120px !important"
                : "100px !important",

            borderRadius: "8px !important",
            margin: "0 auto !important",
            display: "block !important",

            /* Padding adjusted for mobile/desktop */
            padding: isMobile ? "10px 0 !important" : "5px 0 !important",

            /* Text style */
            color: "#fff !important",
            fontFamily: "var(--font-family-main) !important",
            textTransform: "none !important",

            /* Theme-aware background colors */
            backgroundColor:
                themeMode === "light"
                    ? "#272944 !important"
                    : "rgb(54, 56, 83) !important",

            /* Hover state also changes based on theme */
            "&:hover": {
                backgroundColor:
                    themeMode === "light"
                        ? "#1d1f33 !important"
                        : "#2f314f !important",
            },
        }}
        onClick={onClick}
        disabled={disabled}
    >
        {children}
    </Button>
);

export default StyledSubmitButton;
